import client from "./client";

export interface Holiday {
  id: string;
  org_id: string;
  name: string;
  date: string;
  is_paid: boolean;
  description: string | null;
  created_at: string;
}

export interface HolidayCreate {
  name: string;
  date: string;
  is_paid?: boolean;
  description?: string;
}

export interface TenureTier {
  months: number;
  days_per_month: number;
}

export interface LeavePolicy {
  id: string;
  leave_type: string;
  annual_allocation_days: number;
  accrual_method: "annual" | "monthly" | "per_pay_period";
  monthly_accrual_days: number;
  max_carryover_days: number;
  requires_approval: boolean;
  min_notice_days: number;
  max_consecutive_days: number;
  is_active: boolean;
  /** When false (default), requests that would push the employee
   *  below zero are rejected at submit. Toggle on for orgs that
   *  let employees over-draft against next year's accrual. */
  allow_negative_balance?: boolean;
  accrual_cap_days?: number;
  proration_method?: "none" | "days";
  accrue_day_of_month?: number;
  tenure_tiers_json?: TenureTier[] | null;
}

export interface LeavePolicyUpsert {
  leave_type: string;
  annual_allocation_days: number;
  accrual_method: string;
  monthly_accrual_days: number;
  max_carryover_days: number;
  requires_approval: boolean;
  min_notice_days: number;
  max_consecutive_days: number;
  is_active: boolean;
  allow_negative_balance?: boolean;
  accrual_cap_days?: number;
  proration_method?: string;
  accrue_day_of_month?: number;
  tenure_tiers_json?: TenureTier[] | null;
}

export async function listHolidays(year?: number): Promise<Holiday[]> {
  const res = await client.get<Holiday[]>("/leave/holidays", {
    params: year ? { year } : {},
  });
  return res.data;
}

export async function createHoliday(data: HolidayCreate): Promise<Holiday> {
  const res = await client.post<Holiday>("/leave/holidays", data);
  return res.data;
}

export async function updateHoliday(
  id: string,
  data: Partial<HolidayCreate>
): Promise<Holiday> {
  const res = await client.put<Holiday>(`/leave/holidays/${id}`, data);
  return res.data;
}

export async function deleteHoliday(id: string): Promise<void> {
  await client.delete(`/leave/holidays/${id}`);
}

export async function listPolicies(): Promise<LeavePolicy[]> {
  const res = await client.get<LeavePolicy[]>("/leave/policies");
  return res.data;
}

export async function upsertPolicy(
  data: LeavePolicyUpsert
): Promise<LeavePolicy> {
  const res = await client.put<LeavePolicy>("/leave/policies", data);
  return res.data;
}

export interface TeamAvailability {
  on_date: string;
  out: {
    employee_id: string;
    name: string;
    title: string;
    avatar_url: string | null;
    leave_type: string;
  }[];
  available: {
    employee_id: string;
    name: string;
    title: string;
    avatar_url: string | null;
  }[];
}

export async function getTeamAvailability(
  onDate: string
): Promise<TeamAvailability> {
  const res = await client.get<TeamAvailability>("/leave/team-availability", {
    params: { on_date: onDate },
  });
  return res.data;
}

export interface CalendarEntry {
  employee_id: string;
  employee_name: string;
  leave_type: string;
  start_date: string;
  end_date: string;
  status: string;
}

export async function getLeaveCalendar(
  startDate: string,
  endDate: string
): Promise<CalendarEntry[]> {
  const res = await client.get<CalendarEntry[]>("/leave/calendar", {
    params: { start_date: startDate, end_date: endDate },
  });
  return res.data;
}

// -------------------- Accrual runs --------------------

export interface AccrualRunEntry {
  employee_id: string;
  policy_id: string;
  leave_type: string;
  period_key: string;
  days_accrued: number;
  balance_before: number;
  balance_after: number;
  reason: string;
  skipped: boolean;
  skip_reason: string | null;
}

export interface AccrualRunResponse {
  period_key: string;
  dry_run: boolean;
  processed: number;
  accrued_rows: number;
  total_days: number;
  entries: AccrualRunEntry[];
}

export interface AccrualRunRequest {
  period_key?: string;
  dry_run?: boolean;
  leave_type?: string;
}

export async function runAccruals(
  data: AccrualRunRequest
): Promise<AccrualRunResponse> {
  const res = await client.post<AccrualRunResponse>(
    "/leave/accruals/run",
    data
  );
  return res.data;
}

export interface AccrualHistoryRow {
  id: string;
  employee_id: string;
  policy_id: string;
  leave_type: string;
  period_key: string;
  days_accrued: number;
  balance_before: number;
  balance_after: number;
  reason: string;
  created_at: string | null;
}

export async function getAccrualHistory(params?: {
  employee_id?: string;
  limit?: number;
}): Promise<AccrualHistoryRow[]> {
  const res = await client.get<AccrualHistoryRow[]>("/leave/accruals/history", {
    params,
  });
  return res.data;
}
